import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { MessageCircle, BarChart3, Zap, Smartphone, LineChart, Grid3x3 } from 'lucide-react';
import { integrationFeatures } from '../data/mock';

const IntegrationSection = () => {
  const [visibleCards, setVisibleCards] = useState([]);
  
  const iconMap = {
    MessageCircle: MessageCircle,         // WhatsApp Integration - WhatsApp icon
    BarChart3: BarChart3,                 // Web Dashboard - Chart icon
    Zap: Zap,                            // API Integration - n8n-like icon
    Smartphone: Smartphone,               // Mobile Apps - Android icon
    LineChart: LineChart,                 // Advanced Dashboard - Analytics/reporting icon
    Grid3x3: Grid3x3                      // Multi-Platform - Platform grid icon
  };

  const renderIcon = (feature) => {
    if (feature.iconUrl) {
      // Use custom image for WhatsApp and n8n
      return (
        <img 
          src={feature.iconUrl} 
          alt={feature.title}
          className="h-8 w-8 object-contain"
        />
      );
    } else {
      // Use Lucide icon for others
      const IconComponent = iconMap[feature.icon];
      return <IconComponent className="h-8 w-8" />;
    }
  };

  useEffect(() => {
    // Staggered animation for cards (much slower)
    integrationFeatures.forEach((_, index) => {
      setTimeout(() => {
        setVisibleCards(prev => [...prev, index]);
      }, index * 600 + 1200); // 600ms delay between each card, starting after 1200ms
    });
  }, []);

  return (
    <section id="integration" className="py-20 bg-black relative overflow-hidden">
      {/* Glossy Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-tr from-green-600/5 via-transparent to-blue-600/5"></div>
      <div className="absolute top-10 right-20 w-80 h-80 bg-green-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 left-20 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-green-900/30 text-green-300 rounded-full text-sm font-medium mb-6 border border-green-500/20">
            ⚡ Fitur Unggulan
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Satu Sistem untuk 
            <span className="bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent"> Semua Kebutuhan</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            XAI terintegrasi dengan semua platform yang Anda butuhkan. 
            Dari WhatsApp hingga API custom, kelola bisnis dari satu dashboard terpusat.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {integrationFeatures.map((feature, index) => {
            const isVisible = visibleCards.includes(index);
            return (
              <Card 
                key={feature.id}
                className={`group hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-1500 ease-out hover:-translate-y-3 bg-black/60 backdrop-blur-xl border border-white/10 hover:border-white/20 transform ${
                  isVisible 
                    ? 'translate-y-0 opacity-100 scale-100' 
                    : 'translate-y-20 opacity-0 scale-85'
                } ${
                  index % 2 === 0 ? 'hover:bg-gradient-to-br hover:from-black/70 hover:to-gray-900/50' : 'hover:bg-gradient-to-br hover:from-gray-900/50 hover:to-black/70'
                } before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/5 before:to-transparent before:rounded-lg before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-500`}
              >
                <CardContent className="p-8 text-center">
                  <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                    index % 3 === 0 ? 'bg-blue-100 text-blue-600 group-hover:bg-blue-500 group-hover:text-white' :
                    index % 3 === 1 ? 'bg-green-100 text-green-600 group-hover:bg-green-500 group-hover:text-white' :
                    'bg-purple-100 text-purple-600 group-hover:bg-purple-500 group-hover:text-white'
                  }`}>
                    {renderIcon(feature)}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{feature.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Integration Flow */}
        <div className="bg-black/50 backdrop-blur-2xl rounded-3xl p-8 md:p-12 border border-white/10 shadow-2xl shadow-black/40 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-3xl"></div>
          <div className="relative z-10">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Alur Integrasi XAI
            </h3>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Lihat bagaimana XAI mengintegrasikan semua aspek bisnis Anda dalam satu ekosistem yang seamless
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {/* Step 1 */}
            <div className="text-center relative">
              <div className="w-16 h-16 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg">
                1
              </div>
              <h4 className="font-semibold text-white mb-2">Input Data</h4>
              <p className="text-sm text-gray-300">Masukan transaksi via web, mobile, atau WhatsApp</p>
              <div className="hidden md:block absolute top-8 -right-3 w-6 h-0.5 bg-blue-300"></div>
            </div>

            {/* Step 2 */}
            <div className="text-center relative">
              <div className="w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg">
                2
              </div>
              <h4 className="font-semibold text-white mb-2">Proses Otomatis</h4>
              <p className="text-sm text-gray-300">Sistem memproses dan mengkategorikan secara otomatis</p>
              <div className="hidden md:block absolute top-8 -right-3 w-6 h-0.5 bg-green-300"></div>
            </div>

            {/* Step 3 */}
            <div className="text-center relative">
              <div className="w-16 h-16 bg-purple-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg">
                3
              </div>
              <h4 className="font-semibold text-white mb-2">Integrasi Real-time</h4>
              <p className="text-sm text-gray-300">Sinkronisasi data ke semua platform terintegrasi</p>
              <div className="hidden md:block absolute top-8 -right-3 w-6 h-0.5 bg-purple-300"></div>
            </div>

            {/* Step 4 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg">
                4
              </div>
              <h4 className="font-semibold text-white mb-2">Laporan Siap</h4>
              <p className="text-sm text-gray-300">Generate laporan lengkap dan insight bisnis</p>
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntegrationSection;